import * as React from "react";
import { cn } from "../lib/cn";
import { costLabel, costTooltip, extrapolateCost, type CostStatus } from "../lib/cost";
import { formatClock, formatDuration, formatMoney } from "../lib/format";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Tooltip } from "../primitives/menu";
import { useNow, usePrefs } from "./prefs";

export type LiveState = "polling" | "connecting" | "live" | "reconnecting" | "offline";

const dot: Record<LiveState, string> = {
  polling: "bg-success-dot",
  connecting: "bg-neutral-dot",
  live: "bg-success-dot",
  reconnecting: "bg-warning-dot",
  offline: "bg-neutral-dot",
};

/**
 * "● Live · 12s ago" (ui-spec §4.1): time since the last event or bookmark; amber "Reconnecting…" after 30 s
 * disconnected. Pause live updates freezes what the page shows while the watch keeps the cache current (WCAG 2.2.2).
 */
export function LiveIndicator({
  state,
  lastEventAt,
  paused,
  pendingChanges = 0,
  onPausedChange,
}: {
  state: LiveState;
  lastEventAt?: Date;
  paused: boolean;
  pendingChanges?: number;
  onPausedChange: (paused: boolean) => void;
}) {
  const now = useNow(1000);
  let text: string;
  if (paused) text = `Paused · ${pendingChanges} ${pendingChanges === 1 ? "change" : "changes"}`;
  else if (state === "live")
    text = lastEventAt ? `Live · ${formatDuration(Math.max(0, now.getTime() - lastEventAt.getTime()))} ago` : "Live";
  else if (state === "polling") text = "Auto-refresh";
  else if (state === "reconnecting") text = "Reconnecting…";
  else if (state === "offline") text = "Offline";
  else text = "Connecting…";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="live-indicator"
        aria-live="off"
        className="inline-flex items-center gap-2 rounded-md px-2 py-1 text-small text-fg-muted hover:bg-hover"
      >
        <span className={cn("nd-dot", paused ? "bg-neutral-dot" : dot[state])} aria-hidden />
        <span className={state === "reconnecting" && !paused ? "text-warning-fg" : undefined}>{text}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {paused ? (
          <DropdownMenuItem onSelect={() => onPausedChange(false)}>Resume live updates</DropdownMenuItem>
        ) : (
          <DropdownMenuItem onSelect={() => onPausedChange(true)}>Pause live updates</DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Live money on running work (ui-spec §4.4): ticks locally once a second, marked ≈ until the next server value.
 * The accessible name is the last server value, so a screen reader never announces the per-second tick.
 */
export function CostTicker({ cost, asOf, frozen = false }: { cost: CostStatus; asOf: Date; frozen?: boolean }) {
  const prefs = usePrefs();
  const now = useNow(1000);
  const view = extrapolateCost(cost, asOf, now, prefs.tickCosts && !frozen);
  const clock = (t: string) => formatClock(t, prefs.timeZone);
  const serverValue = `${formatMoney(cost.totalUSD)} so far, as of ${formatClock(asOf, prefs.timeZone)}`;
  return (
    <Tooltip content={costTooltip(cost, clock)}>
      <span aria-live="off" aria-label={serverValue} className="tabular-nums">
        {costLabel(view)}
      </span>
    </Tooltip>
  );
}
